import React, { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Kicker } from "@/components/kit";
import { Mark } from "@/components/Logo";
import { ArrowRight, Lock } from "lucide-react";

export default function Gate() {
  const [userId, setUserId] = useState("");
  const [pin, setPin] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      const r = await api.post("/auth/login", { userId: userId.trim().toLowerCase(), pin });
      localStorage.setItem("table_token", r.data.token);
      window.location.href = "/";
    } catch (err) {
      setError(errMsg(err));
      setPin("");
      setState("denied");
    }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[1.05fr_1fr]">
      {/* ceremonial panel */}
      <aside className="panel-ink relative hidden lg:flex flex-col justify-between px-14 py-14 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.14'/%3E%3C/svg%3E\")",
          }}
        />
        <div className="relative flex items-center gap-3">
          <Mark size={34} onDark />
          <span className="text-[12px] tracking-[0.2em] uppercase font-semibold" style={{ color: "#E2E8F0" }}>
            The Table
          </span>
        </div>

        <div className="relative max-w-[26rem]">
          <div className="hair mb-7" style={{ background: "#1e293b", width: 72 }} />
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.12]" style={{ color: "#fff" }}>
            A business that runs <span style={{ color: "#60a5fa" }}>without you</span>.
          </h2>
          <p className="text-[15px] mt-5" style={{ color: "#94a3b8" }}>
            Chief executives. Eight chapters a year. One standard.
          </p>
        </div>

        <div className="relative flex items-center gap-2.5" style={{ color: "#64748b" }}>
          <Lock size={11} />
          <span className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: "inherit" }}>
            What happens at the table, stays at the table
          </span>
        </div>
      </aside>

      {/* gate */}
      <main className="flex items-center justify-center px-6 py-16 sm:px-12">
        <div className="w-full max-w-[26rem] fade-in">
          <div className="flex items-center gap-3 mb-10 lg:hidden">
            <Mark size={34} />
            <span className="text-[12px] tracking-[0.2em] uppercase font-semibold" style={{ color: "var(--ink)" }}>
              The Table
            </span>
          </div>

          <form onSubmit={submit}>
            <Kicker>Members only</Kicker>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.12] mt-3">
              The table is{" "}
              <span style={{ color: "var(--brass-bright)" }}>set</span>.
            </h1>
            <div className="hair mt-5 mb-5" />
            <p className="lede">
              Sign in with your user ID and four-digit PIN. If you do not have one, ask the Moderator.
            </p>

            <div className="mt-9">
              <Kicker>User ID</Kicker>
              <input
                data-testid="gate-user-id"
                required
                autoCapitalize="none"
                autoComplete="username"
                className="field mt-2.5"
                placeholder="firstname"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
              />
            </div>

            <div className="mt-6">
              <Kicker>PIN</Kicker>
              <input
                data-testid="gate-pin"
                required
                type="password"
                inputMode="numeric"
                autoComplete="current-password"
                maxLength={4}
                pattern="\d{4}"
                className="field mt-2.5 numeral tracking-[0.6em]"
                placeholder="••••"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              />
            </div>

            {error && (
              <p data-testid="gate-error" className="text-[13.5px] mt-5" style={{ color: "var(--rust)" }}>
                {error}
              </p>
            )}

            <button
              data-testid="gate-submit"
              className="btn-ink mt-7 w-full"
              disabled={state === "sending" || pin.length !== 4 || !userId.trim()}
            >
              {state === "sending" ? "Checking" : "Take me inside"}
              {state !== "sending" && <ArrowRight size={13} />}
            </button>

            <p className="text-[12.5px] mt-6" style={{ color: "var(--ink-mute)" }}>
              Five wrong PINs locks the user ID for fifteen minutes. Change your PIN any time from
              your own member page.
            </p>

            <p className="kicker !text-[9px] mt-10 lg:hidden">
              What happens at the table, stays at the table
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
